import React, { useEffect, useState } from "react";
import { Modal } from "@carbon/react";
import "./requestProcessingModal.scss";
import { queryClient } from "../..";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { useApproveFRBulk } from "../../hooks/useApproveFRBulk";
import { useDeclineFRBulk } from "../../hooks/useDeclineFRBulk";

export default function RequestProcessingModal({
  modalItem,
  setModalItem,
  setErrorQueue,
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const approveMutation = useApproveFRBulk();
  const declineMutation = useDeclineFRBulk();

  const mapBackendErrorToCode = (error) => {
    if (!error) return null;

    if (error.includes("not enough balance")) {
      return "NO_BALANCE";
    }

    if (error.includes("can not approve")) {
      return "CANNOT_APPROVE";
    }

    if (error.includes("can not cancel")) {
      return "CANNOT_DECLINE";
    }

    return "UNKNOWN";
  };

  const extractErrorsFromBulkResponse = (response, codesMap) => {
    if (!Array.isArray(response?.data)) return [];

    return response.data
      .filter((item) => item.error)
      .map((item) => ({
        kind: "warning",
        code: mapBackendErrorToCode(item.error),
        requestCode: codesMap?.[item.id] ?? "",
      }));
  };

  const mutation =
    modalItem.type === "approve" ? approveMutation : declineMutation;

  const handleSubmit = () => {
    if (submitting) return;

    setSubmitting(true);
    mutation.mutate(
      { ids: modalItem.ids },
      {
        onSuccess: (response) => {
          queryClient.invalidateQueries({ queryKey: ["fuel-requests"] });
          queryClient.invalidateQueries({ queryKey: ["getInfo"] });

          const warnings = extractErrorsFromBulkResponse(
            response.data,
            modalItem.codesMap
          );

          if (warnings.length) {
            setErrorQueue((prev) => [...prev, ...warnings]);
          }
        },
        onError: () => {
          setErrorQueue((prev) => [
            ...prev,
            {
              kind: "error",
              code: "UNKNOWN",
              requestCode: "",
            },
          ]);
        },
        onSettled: () => {
          setSubmitting(false);
          setModalItem({ open: false, type: null, ids: [] });
        },
      }
    );
  };

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: "FuelRequestBulkModal",
      firebase_screen_class: "FuelRequest",
    });
  }, []);

  return (
    <Modal
      open={modalItem.open}
      onRequestClose={() => setModalItem({ open: false })}
      modalHeading={`${t("main.fuel.fuelRequestsModal.title", {
        type:
          modalItem.type === "approve"
            ? t("main.fuel.fuelRequestsModal.approveSubmit")
            : t("main.fuel.fuelRequestsModal.declineSubmit"),
      })}`}
      modalLabel={`${t("main.fuel.fuelRequestsModal.label")}`}
      primaryButtonDisabled={submitting}
      primaryButtonText={
        modalItem.type === "approve"
          ? `${t("main.fuel.fuelRequestsModal.approveSubmit")}`
          : `${t("main.fuel.fuelRequestsModal.declineSubmit")}`
      }
      secondaryButtonText={`${t("main.fuel.fuelRequestsModal.cancel")}`}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={() => setModalItem({ open: false })}
      closeButtonLabel={`${t("main.modalText.close")}`}
      size="sm"
    >
      <div>
        {modalItem.type === "approve"
          ? t("main.fuel.fuelRequestsModal.approveDesc", {
              fuel: modalItem.totalFuel,
              client: modalItem.client,
            })
          : t("main.fuel.fuelRequestsModal.declineDesc", {
              fuel: modalItem.totalFuel,
              client: modalItem.client,
            })}
      </div>
    </Modal>
  );
}
